import { ReactNode } from "react";

import { ErrorBoundary } from "@sentry/react";

import { captureException } from "~/integrations/posthog/client";

interface SentryErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

const DefaultFallback = () => (
  <div className="flex min-h-screen items-center justify-center">
    <div className="text-center">
      <h1 className="mb-4 text-2xl font-bold text-red-600">Algo salió mal</h1>
      <p className="mb-4 text-gray-600">
        Ha ocurrido un error inesperado. El equipo técnico ha sido notificado.
      </p>
      <button
        onClick={() => window.location.reload()}
        className="rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
      >
        Recargar página
      </button>
    </div>
  </div>
);

export const SentryErrorBoundary = ({
  children,
  fallback,
}: SentryErrorBoundaryProps) => {
  return (
    <ErrorBoundary
      fallback={fallback || <DefaultFallback />}
      showDialog
      onError={(error, componentStack) => {
        // Surface the crash in PostHog as an `$exception` so it appears on the
        // session-replay timeline (the Sentry boundary reports only to Sentry).
        captureException(error, {
          $exception_source: "react-error-boundary",
          component_stack: componentStack,
          url: typeof window !== "undefined" ? window.location.href : undefined,
        });
        // Always log (not just in DEV) so the error text is captured in the
        // PostHog replay console and the browser console in production too.
        // eslint-disable-next-line no-console
        console.error(
          "[ErrorBoundary] Uncaught render error:",
          error,
          componentStack
        );
      }}
    >
      {children}
    </ErrorBoundary>
  );
};
